import { useState, useCallback, useRef, useEffect } from 'react'
import { useSignMessage } from 'wagmi'
import { getFriendlyTransactionError } from '@/utils/walletError.ts'
import { useWallet } from './useWallet.ts'
import { encryption } from '@/services/index.ts'
import type {
  EncryptionResult,
  DecryptionResult,
  AuthSession,
} from '@/services/encryption/encryption.types.ts'

interface UseEncryptionState {
  isConnected: boolean
  isAuthenticated: boolean
  loading: boolean
  error: string | null
}

interface UseEncryptionReturn extends UseEncryptionState {
  connect: () => Promise<void>
  disconnect: () => Promise<void>
  authenticate: () => Promise<AuthSession | null>
  getLastError: () => string | null

  /**
   * BL-X-01: contractAddress and chain are no longer accepted at the call
   * site. The proxy pins them to the server-side `CONTRACT_ADDRESS` /
   * `CONTRACT_NETWORK` env vars so a malicious client cannot redirect the
   * Lit Action's `canDecrypt()` check at a fake contract.
   */
  encryptString: (
    data: string,
    benefactorAddress: string,
  ) => Promise<EncryptionResult | null>

  encryptFile: (
    file: File | Blob | Uint8Array,
    benefactorAddress: string,
  ) => Promise<EncryptionResult | null>

  decrypt: (
    ciphertext: string,
    dataToEncryptHash: string,
    vaultId: number | string,
    beneficiaryAddress: string,
  ) => Promise<DecryptionResult | null>

  decryptToString: (
    ciphertext: string,
    dataToEncryptHash: string,
    vaultId: number | string,
    beneficiaryAddress: string,
  ) => Promise<string | null>

  decryptToFile: (
    ciphertext: string,
    dataToEncryptHash: string,
    vaultId: number | string,
    beneficiaryAddress: string,
  ) => Promise<Blob | null>
}

export function useEncryption(): UseEncryptionReturn {
  const { address } = useWallet()
  const { signMessageAsync } = useSignMessage()
  const authSessionRef = useRef<AuthSession | null>(null)
  const lastErrorRef = useRef<string | null>(null)

  const [state, setState] = useState<UseEncryptionState>({
    isConnected: false,
    isAuthenticated: false,
    loading: false,
    error: null,
  })

  const setLoading = () =>
    setState((s) => ({ ...s, loading: true, error: null }))
  const setError = (error: string) => {
    lastErrorRef.current = error
    setState((s) => ({ ...s, loading: false, error }))
  }

  useEffect(() => {
    return () => {
      if (encryption.isReady()) {
        encryption.disconnect()
      }
    }
  }, [])

  const connect = useCallback(async () => {
    setLoading()
    try {
      await encryption.connect()
      setState((s) => ({ ...s, isConnected: true, loading: false }))
    } catch (e) {
      setError(getFriendlyTransactionError(e))
    }
  }, [])

  const disconnect = useCallback(async () => {
    try {
      await encryption.disconnect()
      authSessionRef.current = null
      setState({
        isConnected: false,
        isAuthenticated: false,
        loading: false,
        error: null,
      })
    } catch (e) {
      setError(getFriendlyTransactionError(e))
    }
  }, [])

  const authenticate = useCallback(async (): Promise<AuthSession | null> => {
    if (!address) {
      setError('Wallet not connected')
      return null
    }
    if (!encryption.isReady()) {
      setError('Encryption service not connected. Call connect() first.')
      return null
    }

    setLoading()
    try {
      const session = await encryption.authenticate({
        walletAddress: address as `0x${string}`,
        signMessage: async (message: string) => {
          if (!signMessageAsync) throw new Error('Sign message not available')
          return signMessageAsync({ message })
        },
      })
      authSessionRef.current = session
      setState((s) => ({ ...s, isAuthenticated: true, loading: false }))
      return session
    } catch (e) {
      setError(getFriendlyTransactionError(e))
      return null
    }
  }, [address, signMessageAsync])

  const encryptString = useCallback(
    async (
      data: string,
      benefactorAddress: string,
    ): Promise<EncryptionResult | null> => {
      setLoading()
      try {
        const result = await encryption.encryptString({
          data,
          benefactorAddress,
          signMessage: async (message: string) => {
            if (!signMessageAsync) throw new Error('Sign message not available')
            return signMessageAsync({ message })
          },
        })
        setState((s) => ({ ...s, loading: false }))
        return result
      } catch (e) {
        setError(getFriendlyTransactionError(e))
        console.error('[useEncryption] encryptString failed:', e)
        return null
      }
    },
    [signMessageAsync],
  )

  const encryptFile = useCallback(
    async (
      file: File | Blob | Uint8Array,
      benefactorAddress: string,
    ): Promise<EncryptionResult | null> => {
      setLoading()
      try {
        const result = await encryption.encryptFile({
          file,
          benefactorAddress,
          signMessage: async (message: string) => {
            if (!signMessageAsync) throw new Error('Sign message not available')
            return signMessageAsync({ message })
          },
        })
        setState((s) => ({ ...s, loading: false }))
        return result
      } catch (e) {
        setError(getFriendlyTransactionError(e))
        console.error('[useEncryption] encryptFile failed:', e)
        return null
      }
    },
    [signMessageAsync],
  )

  const decrypt = useCallback(
    async (
      ciphertext: string,
      dataToEncryptHash: string,
      vaultId: number | string,
      beneficiaryAddress: string,
    ): Promise<DecryptionResult | null> => {
      setLoading()
      try {
        const result = await encryption.decrypt({
          ciphertext,
          dataToEncryptHash,
          vaultId,
          beneficiaryAddress,
        })
        setState((s) => ({ ...s, loading: false }))
        return result
      } catch (e) {
        setError(getFriendlyTransactionError(e))
        return null
      }
    },
    [],
  )

  const decryptToString = useCallback(
    async (
      ciphertext: string,
      dataToEncryptHash: string,
      vaultId: number | string,
      beneficiaryAddress: string,
    ): Promise<string | null> => {
      setLoading()
      try {
        const result = await encryption.decryptToString({
          ciphertext,
          dataToEncryptHash,
          vaultId,
          beneficiaryAddress,
        })
        setState((s) => ({ ...s, loading: false }))
        return result
      } catch (e) {
        setError(getFriendlyTransactionError(e))
        return null
      }
    },
    [],
  )

  const decryptToFile = useCallback(
    async (
      ciphertext: string,
      dataToEncryptHash: string,
      vaultId: number | string,
      beneficiaryAddress: string,
    ): Promise<Blob | null> => {
      setLoading()
      try {
        const result = await encryption.decryptToFile({
          ciphertext,
          dataToEncryptHash,
          vaultId,
          beneficiaryAddress,
        })
        setState((s) => ({ ...s, loading: false }))
        return result
      } catch (e) {
        setError(getFriendlyTransactionError(e))
        return null
      }
    },
    [],
  )

  const getLastError = useCallback(() => lastErrorRef.current, [])

  return {
    ...state,
    getLastError,
    connect,
    disconnect,
    authenticate,
    encryptString,
    encryptFile,
    decrypt,
    decryptToString,
    decryptToFile,
  }
}
